import { useState } from "react";
import { AppBar } from "react-admin";
import { useContext } from "react";
import {
  IconButton,
  Badge,
  Popover,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Button,
  keyframes,
  CircularProgress,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import DoneIcon from "@mui/icons-material/Done";
import MyUserMenu from "./MyUserMenu";
import { useNotification } from "./NotificationContext";
import OnboardingSheet from "./components/OnboardingSheet";
import { OnboardingContext } from "./contexts/OnboardingContext";

// Animación que sugiere hacer click
const clickSuggestion = keyframes`
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(25, 118, 210, 0.3);
  }
  25% {
    transform: scale(1.05);
  }
  50% {
    transform: scale(1);
    box-shadow: 0 0 0 6px rgba(25, 118, 210, 0.1);
  }
  75% {
    transform: scale(1.05);
  }
`;

const MyAppBar = (props) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const { notifications, removeNotification } = useNotification();
  const profile = JSON.parse(localStorage.getItem("profile"));
  const { onboardingSteps, setShouldOpenOnboarding } = useContext(OnboardingContext);

  // Define los pasos totales (ajusta según tu lógica)
  const totalSteps = 7;
  const completedSteps = Array.isArray(onboardingSteps) ? onboardingSteps.length : 0;
  const percentage = totalSteps > 0 ? (completedSteps / totalSteps) * 100 : 0;

  // Verificar si el onboarding está completo
  const isOnboardingComplete = completedSteps >= totalSteps;

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMarkAsRead = (id) => {
    removeNotification(id);
  };

  const handleOnboardingOpen = () => {
    setShouldOpenOnboarding(true);
  };

  const handleOnboardingClose = () => {
    setShouldOpenOnboarding(false);
  };

  const open = Boolean(anchorEl);
  const id = open ? "notification-popover" : undefined;

  return (
    <>
      <AppBar {...props} userMenu={<MyUserMenu />}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            justifyContent: "flex-end",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {/* Botón de Primeros pasos - Solo mostrar si no está al 100% */}
            {profile?.role !== "admin" && !isOnboardingComplete && (
              <Button
                onClick={handleOnboardingOpen}
                size="small"
                sx={{
                  color: "primary.main",
                  bgcolor: "rgba(25, 118, 210, 0.04)",
                  border: "1px solid rgba(255, 152, 0, 0.3)",
                  borderRadius: 2,
                  px: 1.5,
                  py: 0.3,
                  textTransform: "none",
                  fontWeight: "500",
                  fontSize: "0.7rem",
                  minWidth: "auto",
                  boxShadow: "0 2px 6px rgba(255, 152, 0, 0.15), 0 1px 3px rgba(0, 0, 0, 0.1)",
                  animation: `${clickSuggestion} 3s ease-in-out infinite`,
                  transition: "all 0.2s ease",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.8,
                  "&:hover": {
                    bgcolor: "rgba(25, 118, 210, 0.04)",
                    border: "1px solid rgba(255, 152, 0, 0.5)",
                    boxShadow: "0 4px 12px rgba(255, 152, 0, 0.2), 0 2px 6px rgba(0, 0, 0, 0.15)",
                    transform: "translateY(-1px)",
                  },
                  "&:active": {
                    transform: "translateY(0px)",
                    boxShadow: "0 2px 6px rgba(255, 152, 0, 0.15), 0 1px 3px rgba(0, 0, 0, 0.1)",
                  },
                }}
              >
                {/* Círculo de progreso */}
                <Box sx={{ position: "relative", display: "inline-flex" }}>
                  {/* Círculo de fondo */}
                  <CircularProgress
                    variant="determinate"
                    value={100}
                    size={27}
                    thickness={3.5}
                    sx={{
                      color: "rgba(25, 118, 210, 0.1)",
                      position: "absolute",
                    }}
                  />
                  {/* Círculo de progreso */}
                  <CircularProgress
                    variant="determinate"
                    value={percentage}
                    size={27}
                    thickness={3.5}
                    sx={{
                      color: "primary.main",
                      transform: "rotate(-90deg) !important",
                    }}
                  />
                  {/* Texto del progreso */}
                  <Box
                    sx={{
                      top: 0,
                      left: 0,
                      bottom: 0,
                      right: 0,
                      position: "absolute",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      variant="caption"
                      component="div"
                      sx={{
                        fontSize: "0.55rem",
                        fontWeight: "600",
                        color: "primary.main",
                      }}
                    >
                      {completedSteps}/{totalSteps}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                  <Typography variant="body2" sx={{ fontSize: "0.7rem", fontWeight: "500" }}>
                    Primeros pasos
                  </Typography>
                </Box>
              </Button>
            )}

            {profile?.role !== "admin" && (
              <>
                <IconButton color="inherit" onClick={handleOpen}>
                  <Badge badgeContent={notifications.length} color="error">
                    <NotificationsIcon />
                  </Badge>
                </IconButton>

                <Popover
                  id={id}
                  open={open}
                  anchorEl={anchorEl}
                  onClose={handleClose}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                  PaperProps={{ sx: { width: 300, p: 1 } }}
                >
                  <Typography variant="h6" sx={{ px: 2, py: 1 }}>
                    Notificaciones
                  </Typography>
                  <Divider />
                  <List dense>
                    {notifications.length === 0 ? (
                      <Typography variant="body2" sx={{ p: 2 }}>
                        No hay notificaciones.
                      </Typography>
                    ) : (
                      notifications.map((n) => (
                        <ListItem key={n.id} alignItems="flex-start">
                          <ListItemText primary={n.title} secondary={n.message} />
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              aria-label="marcar como leído"
                              onClick={() => handleMarkAsRead(n.id)}
                            >
                              <DoneIcon />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))
                    )}
                  </List>
                </Popover>
              </>
            )}
          </Box>
        </Box>
      </AppBar>

      {/* Sheet de Onboarding - Solo renderizar si no está completo */}
      {!isOnboardingComplete && <OnboardingSheet onClose={handleOnboardingClose} />}
    </>
  );
};

export default MyAppBar;
